import React, { useEffect, useState } from "react";
import { connect } from "react-redux";
import DashboardLayout from "../layout/Dashboard";
import { FormattedMessage } from "react-intl";
import { Link } from "react-router-dom";
import { getOrganizationDetails as getOrganizationDetailsApi } from "../../api/clients/organizations";
import { Messages } from "../../components/layout/Messages/Messages";
import { ROUTE_CLIENT_ORGANIZATIONS } from "../../config/routes";
import { history } from "../../store/configureStore";
import {
  NOTIFICATION_ARCHIVE_SERVICE_ACTIONS,
  NOTIFICATION_VIEW_ORGANIZATION
} from '../../config/notifications';
import { ACL, ORGANIZATION_STATUSES, PAGE_CONTEXTS, TRANSLATED_ORGANIZATION_STATUSES } from '../../config/constants';
import { NoRights } from '../../components/layout/Permissions/NoRights';
import { store } from '../../store/store';
import { hideNotification } from '../../store/actions/notification';
import { NOTIFICATION_ERROR } from '../../store/actions/types';
import { DezactivateArchiveService } from "../../components/pages/organizations/table/actionButtons/DezactivateArchiveService";
import { ReactivateArchiveService } from "../../components/pages/organizations/table/actionButtons/ReactivateArchiveService";
import { OrganizationDetails } from "../../components/pages/organizations/OrganizationDetails";
import { getUsers as getOrganizationUsersApi } from "../../api/clients/users";
import { getOrdersApi as getOrganizationOrdersApi } from "../../api/orders/orders";
import {
  PageHeader,
  PageSurface,
  PageWorkspace
} from '../../components/layout/Workspace';
import './ViewOrganization.scss';

function ViewOrganizationComponent(props) {
  const { organizationId } = props.match.params;
  const [ organization, setOrganization ] = useState( null );

  const [ users, setUsers ] = useState( null );
  const [ metaUsers, setMetaUsers ] = useState( null );
  const [ rowsPerPageUsers, setRowsPerPageUsers ] = useState( 10 );

  const [ orders, setOrders ] = useState( null );
  const [ metaOrders, setMetaOrders ] = useState( null );
  const [ rowsPerPageOrders, setRowsPerPageOrders ] = useState( 10 );

  useEffect( () => {
    getOrganizationDetails( organizationId );
    getOrganizationUsers( organizationId, 1 );
    getOrganizationOrders( organizationId, 1 );

    return () => {
      if (store.getState().notification.type === NOTIFICATION_ERROR) {
        store.dispatch(hideNotification());
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [] );

  /**
   * Get organization details
   * @param {string} [organizationId]
   */
  const getOrganizationDetails = (organizationId) => {
    if (! ACL.CLIENT.USERS.VIEW.hasAccess()) {
      return;
    }

    getOrganizationDetailsApi( {
      organizationId: organizationId,
      notificationId: NOTIFICATION_VIEW_ORGANIZATION,
      pagination: {
        page_context: PAGE_CONTEXTS.CLIENT_ORGANIZATION.VIEW,
      }
    } ).then( (response) => {
      if (response && response.data && response.data.id) {
        setOrganization( response.data );
      }
    } );
  };

  /**
   * Update organization in state after a success update on server
   * @object value
   */
  const updateOrganization = (value) => {
    setOrganization( value );
  }

  /**
   * Get organization users
   * @param {string} [organizationId]
   * @param {number} [page]
   * @param {string} [sortField]
   * @param {string} [sortDirection]
   * @param {string} [searchText]
   * @param {boolean} [hideNotification]
   */
  const getOrganizationUsers = (organizationId, page, sortField, sortDirection, searchText, hideNotification = false) => {
    if (! ACL.CLIENT.USERS.LIST.hasAccess()) {
      return;
    }
    getOrganizationUsersApi( {
      pagination: {
        page: page,
        sort_by: sortField,
        sort: sortDirection,
        q: searchText,
        per_page: rowsPerPageUsers,
        page_context: PAGE_CONTEXTS.CLIENT_USER.LIST,
        for_organization: organizationId,
      },
      hideNotification: hideNotification
    } ).then( (response) => {

      setUsers( response.data );
      setMetaUsers( response.meta );
    } );
  };

  /**
   * Get organization orders
   * @param {string} [organizationId]
   * @param {number} [page]
   * @param {string} [sortField]
   * @param {string} [sortDirection]
   * @param {string} [searchText]
   * @param {boolean} [hideNotification]
   */
  const getOrganizationOrders = (organizationId, page, sortField, sortDirection, searchText, hideNotification = false) => {
    if (! ACL.CLIENT.ORDERS.LIST.hasAccess()) {
      return;
    }
    getOrganizationOrdersApi( {
      pagination: {
        page: page,
        sort_by: sortField,
        sort: sortDirection,
        q: searchText,
        per_page: rowsPerPageOrders,
        page_context: PAGE_CONTEXTS.ORDERS.LIST,
        for_organization: organizationId,
      },
      hideNotification: hideNotification
    } ).then( (response) => {

      setOrders( response.data );
      setMetaOrders( response.meta );
    } );
  };

  const backLocation = history.location.state && history.location.state.prevLocation !== null
    ? history.location.state.prevLocation
    : ROUTE_CLIENT_ORGANIZATIONS;
  const organizationStatusModifier = organization && organization.status === ORGANIZATION_STATUSES.CREATED
    ? 'created'
    : 'muted';
  const pageHeader = (
    <PageHeader
      actions={organization?.id && organization?.archive_service_setting?.id ? (
        <>
          <DezactivateArchiveService
            archiveServiceSetting={organization.archive_service_setting}
            index={1}
            actionCallback={() => history.push( ROUTE_CLIENT_ORGANIZATIONS )}
          />
          <ReactivateArchiveService
            archiveServiceSetting={organization.archive_service_setting}
            index={2}
            actionCallback={() => history.push( ROUTE_CLIENT_ORGANIZATIONS )}
          />
        </>
      ) : null}
      breadcrumbs={(
        <Link className="organization-detail-back-link" to={backLocation}>
          <i aria-hidden="true" className="fa fa-arrow-left"/>
          <FormattedMessage id="messages.go_back"/>
        </Link>
      )}
      status={organization?.id ? (
        <span className={`organization-detail-status organization-detail-status--${organizationStatusModifier}`}>
          <span aria-hidden="true" className="organization-detail-status__dot"/>
          {TRANSLATED_ORGANIZATION_STATUSES[organization.status]}
        </span>
      ) : null}
      title={organization?.name || <FormattedMessage id="pages.organization.title"/>}
    />
  );
  const hasNoPageRights = ! ACL.CLIENT.USERS.VIEW.hasAccess();

  return (
    <DashboardLayout location={props.location} className="dashboard" workspace>
      <PageWorkspace className="organization-detail-workspace">
        <div className="organization-detail-notifications">
          <Messages notificationId={NOTIFICATION_ARCHIVE_SERVICE_ACTIONS}/>
          <Messages notificationId={NOTIFICATION_VIEW_ORGANIZATION}/>
        </div>
        {hasNoPageRights ? (
          <>
            {pageHeader}
            <PageSurface>
              <NoRights/>
            </PageSurface>
          </>
        ) : (
          <OrganizationDetails
            header={pageHeader}
            location={props.location}
            organization={organization}
            organizationId={organizationId}
            updateOrganization={updateOrganization}
            users={users}
            metaUsers={metaUsers}
            getOrganizationUsers={getOrganizationUsers}
            rowsPerPageUsers={rowsPerPageUsers}
            setRowsPerPageUsers={setRowsPerPageUsers}
            orders={orders}
            metaOrders={metaOrders}
            getOrganizationOrders={getOrganizationOrders}
            rowsPerPageOrders={rowsPerPageOrders}
            setRowsPerPageOrders={setRowsPerPageOrders}
          />
        )}
      </PageWorkspace>
    </DashboardLayout>
  );
}

const mapStateToProps = (state) => ( {} );

const ViewOrganization = connect( mapStateToProps, null )( ViewOrganizationComponent );

export { ViewOrganization, ViewOrganizationComponent };
